<template>
  <OverlayCard width="l" @cancel="$emit('cancel')">
    <template #title>
      {{ stepTitle }}
    </template>

    <template #content>
      <div class="onboarding__content">
        <transition name="logo-transition" mode="out-in" @enter="initVideoIfNeeded">
          <div v-if="shouldShowVideo">
            <div ref="player" data-plyr-provider="youtube" :data-plyr-embed-id="videoId"></div>
          </div>
          <p v-else v-html="stepContent" :key="step"></p>
        </transition>
      </div>
    </template>

    <template #footer>
      <transition name="modal-transition" mode="out-in">
        <div class="onboarding__footergrid" v-if="step < total - 1" key="0">
          <Btn
            color="grey"
            size="medium"
            data-test-id="onboarding-close-button"
            @click="$store.dispatch('onboarding/hide')"
          >
            {{ $t('onboarding.close') }}
          </Btn>

          <StepDots :total="total" :step="step" @change="goto" />

          <Btn color="blue" icon data-test-id="onboarding-next-button" @click="step++">
            <ArrowRightIcon16 />
          </Btn>
        </div>
      </transition>

      <div class="onboarding__footerflex" v-if="showUpgradeOrTrialButtons && step === total - 1" key="1">
        <LicenseTrialAction v-if="canHaveTrial" />

        <LicenseUpgradeAction :color="canHaveTrial ? 'blue' : 'grey'" />

        <Btn v-if="isInTrial" color="grey" size="medium" data-test-id="license-start-trial-button" @click="finish">
          {{ $t('licensing.action.startTrial') }}
        </Btn>
      </div>
    </template>
  </OverlayCard>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { length, keys } from 'ramda';
import Plyr from 'plyr';

import { LeaseState } from '@common/license/types';
import LicenseUpgradeAction from '@components/license/LicenseUpgradeAction.vue';
import LicenseTrialAction from '@components/license/LicenseTrialAction.vue';
import ArrowRightIcon16 from '@components/Icons/ArrowRightIcon16.vue';
import { GeneralEvent } from '@/tracking/trackingTypes';

export default defineComponent({
  emits: { cancel: null },
  name: 'Onboarding',

  components: {
    ArrowRightIcon16,
    LicenseUpgradeAction,
    LicenseTrialAction,
  },

  props: {
    canHaveTrial: {
      type: Boolean,
      required: true,
    },

    leaseState: {
      type: String,
      required: true,
    },

    isInTrial: {
      type: Boolean,
      required: true,
    },
  },

  data(): {
    step: number;
    videoId: string;
  } {
    let videoId: string = '';

    switch (this.$i18n.locale) {
      case 'es':
        videoId = 'Q6e2UldF9N8';
        break;
      case 'en':
      default:
        videoId = 'v78gSmhK2-o';
    }

    return {
      step: 0,
      videoId,
    };
  },

  mounted() {
    this.initVideoIfNeeded();
  },

  computed: {
    shouldShowVideo(): boolean {
      return this.step === 0;
    },

    stepTitle(): string {
      return this.$t(`onboarding.steps.${this.step}.title`);
    },
    stepContent(): string {
      return this.$t(`onboarding.steps.${this.step}.content`, { supportMail: this.$config.supportMail });
    },

    total(): number {
      const total = length(keys(this.$i18n.messages.en.onboarding.steps));

      // If user has lease, don't show upgrade / trial
      return this.leaseState === LeaseState.Ok ? total - 1 : total;
    },

    showUpgradeOrTrialButtons(): boolean {
      return this.leaseState !== LeaseState.Ok || this.isInTrial;
    },
  },

  watch: {
    step(value) {
      if (value === this.total - 1) {
        this.$tracker.track(GeneralEvent.OnboardingLastStepReached);
      }
      this.initVideoIfNeeded();
    },
  },

  methods: {
    finish() {
      this.$store.dispatch('onboarding/hide');
    },

    goto(step: number) {
      this.step = step;
    },

    initVideoIfNeeded() {
      if (this.shouldShowVideo) {
        return new Plyr(this.$refs.player as HTMLElement);
      }

      return null;
    },
  },
});
</script>

<style lang="scss">
:root {
  --plyr-video-background: rgb(255, 255, 255);
}

.onboarding__footergrid {
  flex: 1 1 0;
  display: grid;
  grid-template-columns: 1fr min-content min-content;
  align-items: center;
  justify-items: start;
  gap: 16px;
}

// this is the same like the normal footer, but for the transition to work
// we need to have an extra element. is there a better way? have not put much thought into it yet...
.onboarding__footerflex {
  display: flex;
  gap: 8px;
  align-items: center;
}

.onboarding__content iframe {
  width: 100%;
}
</style>
